import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const loadingSpinnerVariants = cva(
  "animate-spin rounded-full border-2 border-current border-t-transparent",
  {
    variants: {
      size: {
        sm: "h-4 w-4",
        default: "h-6 w-6",
        lg: "h-8 w-8",
        xl: "h-10 w-10",
      },
    },
    defaultVariants: {
      size: "default",
    },
  }
);

const loadingSkeletonVariants = cva(
  "animate-pulse rounded-md bg-muted",
  {
    variants: {
      variant: {
        default: "bg-muted",
        card: "bg-muted rounded-lg",
        text: "bg-muted rounded",
        circular: "bg-muted rounded-full",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

export interface LoadingSpinnerProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof loadingSpinnerVariants> {}

export interface LoadingSkeletonProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof loadingSkeletonVariants> {}

const LoadingSpinner = React.forwardRef<HTMLDivElement, LoadingSpinnerProps>(
  ({ className, size, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(loadingSpinnerVariants({ size, className }))}
        {...props}
      />
    );
  }
);
LoadingSpinner.displayName = "LoadingSpinner";

const LoadingSkeleton = React.forwardRef<HTMLDivElement, LoadingSkeletonProps>(
  ({ className, variant, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(loadingSkeletonVariants({ variant, className }))}
        {...props}
      />
    );
  }
);
LoadingSkeleton.displayName = "LoadingSkeleton";

const LoadingCard = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => {
  return (
    <div className={cn("p-6 space-y-3", className)} {...props}>
      <LoadingSkeleton className="h-4 w-1/2" />
      <LoadingSkeleton className="h-8 w-3/4" />
      <LoadingSkeleton className="h-3 w-full" />
      <LoadingSkeleton className="h-3 w-2/3" />
    </div>
  );
};
LoadingCard.displayName = "LoadingCard";

const LoadingTable = ({ 
  rows = 5, 
  columns = 4, 
  className, 
  ...props 
}: React.HTMLAttributes<HTMLDivElement> & { rows?: number; columns?: number }) => {
  return (
    <div className={cn("space-y-3", className)} {...props}>
      {/* Table header */}
      <div className="flex space-x-4">
        {Array.from({ length: columns }, (_, i) => (
          <LoadingSkeleton key={i} className="h-4 flex-1" />
        ))}
      </div>
      {/* Table rows */}
      {Array.from({ length: rows }, (_, rowIndex) => (
        <div key={rowIndex} className="flex space-x-4">
          {Array.from({ length: columns }, (_, colIndex) => (
            <LoadingSkeleton key={colIndex} className="h-6 flex-1" />
          ))}
        </div>
      ))}
    </div>
  );
};
LoadingTable.displayName = "LoadingTable";

const LoadingButton = ({ 
  children, 
  className, 
  ...props 
}: React.ButtonHTMLAttributes<HTMLButtonElement>) => {
  return (
    <button
      className={cn(
        "inline-flex items-center gap-2 px-4 py-2 rounded-md bg-muted text-muted-foreground cursor-not-allowed",
        className
      )}
      disabled
      {...props}
    >
      <LoadingSpinner size="sm" />
      {children}
    </button>
  );
};
LoadingButton.displayName = "LoadingButton";

const LoadingPage = ({ 
  className, 
  ...props 
}: React.HTMLAttributes<HTMLDivElement>) => {
  return (
    <div className={cn("space-y-6", className)} {...props}>
      {/* Header */}
      <div className="space-y-2">
        <LoadingSkeleton className="h-8 w-1/3" />
        <LoadingSkeleton className="h-4 w-1/2" />
      </div>
      
      {/* Content cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Array.from({ length: 6 }, (_, i) => (
          <LoadingCard key={i} className="border rounded-lg" />
        ))}
      </div>
      
      {/* Table */}
      <div className="border rounded-lg p-4">
        <LoadingTable rows={8} columns={5} />
      </div>
    </div>
  );
};
LoadingPage.displayName = "LoadingPage";

export {
  LoadingSpinner,
  LoadingSkeleton,
  LoadingCard,
  LoadingTable,
  LoadingButton,
  LoadingPage,
  loadingSpinnerVariants,
  loadingSkeletonVariants,
};
